import {ObjectRepository} from "@api/repositories/ObjectRepository";
import {RatingRepository} from "@api/repositories/RatingRepository";
import {RepositoryFabric} from "../../../api/repositories/RepositoryFabric";
import {Element} from "@api/models/Element";
import {EventMembershipTypes} from "../../../api/enums/EventMembershipTypes";
import {EventMembershipRepository} from "@api/repositories/EventMembershipRepository";
import {EventMembership} from "@api/models/EventMemberShip";
import Vue from 'vue';
import {WebinarInfo} from "@api/models/WebinarInfo";
import {SessionRepository} from "@api/repositories/SessionRepository";
import {Schema} from "@api/models/Schema";
import {DateTime} from "luxon";
import {Rating} from "@api/models/Rating";
import {UserRepository} from "@api/repositories/UserRepository";
import {CommentRepository} from "@api/repositories/CommentRepository";
import {Observers} from "@api/repositories/reatime-repository/types";
import {RealtimeRepository} from "@api/repositories/reatime-repository/RealtimeRepository";
import { Store } from "vuex";

export class SessionDataHelper {
    private readonly objectRepository: ObjectRepository;
    private readonly ratingRepository: RatingRepository;
    private readonly eventMembershipRepository: EventMembershipRepository;
    private readonly sessionRepository: SessionRepository;
    realtimeRepository: RealtimeRepository;
    private readonly commentRepository: CommentRepository;
    private readonly userRepository: UserRepository;
    objectId: string;
    element?: Element;
    agendaSchema: Schema;
    speakers: EventMembership[] = [];
    tags: Element[] = [];
    webinarInfo: WebinarInfo;
    tagSchema = '';
    areaSchema = '';
    area: Element;
    likes: Rating;
    commentsCount = 0;
    speakersFetched = false;

    constructor(repositories: RepositoryFabric, private store: Store<any>, private agendaSchemaId = process.env.VUE_APP_AGENDA_SCHEMA!) {
        this.objectRepository = repositories.get(RepositoryFabric.OBJECT);
        this.ratingRepository = repositories.get(RepositoryFabric.RATING);
        this.eventMembershipRepository = repositories.get(RepositoryFabric.EVENT_MEMBERSHIP);
        this.sessionRepository = repositories.get(RepositoryFabric.SESSION);
        this.userRepository = repositories.get(RepositoryFabric.USER);
        this.commentRepository = repositories.get(RepositoryFabric.COMMENT);
        this.realtimeRepository = repositories.get(RepositoryFabric.REALTIME);
        this.tagSchema = 'Tags';
        this.areaSchema = 'Areas';
    }

    async setObjectId(id: string) {
        Vue.set(this, 'objectId', id);
        await this.fetchObject();
    }

    async fetchSupportingData() {
        await Promise.all([
            this.fetchSpeakers(),
            this.fetchLikes(),
            this.fetchTags(),
            this.fetchArea(),
            this.fetchCommentsAmount(),
        ]);
    }

    reset() {
        this.objectId = '';
    }

    async attachToRealtimeUpdate(observers: Observers) {
        await this.realtimeRepository.attach(observers);
    }

    async detachFromRealtimeUpdate(observers: Observers) {
        await this.realtimeRepository.detach(observers);
    }


    private getAgendaQueryConditions(): any {
        const where = {
            id: this.objectId,
            groupIds: {
                $containsAnyOrNullOrEmpty: this.store.state.session.profile?.groupIds || []
            }
        } as any

        if (this.store.state.webConferenceRecordingEventId) {
            delete where.groupIds;
        }

        return where;
    }

    private async fetchObject() {
        const element = (
            await this.objectRepository.query(this.agendaSchemaId, {
                where: this.getAgendaQueryConditions()
            })
        )[0];

        Vue.set(this, 'element', element);

        if (this.element?.beginAt) {
            this.element.beginAt = DateTime.fromISO(this.element.beginAt);
        }
        if (this.element?.endAt) {
            this.element.endAt = DateTime.fromISO(this.element.endAt);
        }
    }

    private async fetchSpeakers() {
        this.speakersFetched = false;
        const speakers = await this.eventMembershipRepository.queryParticipants(this.agendaSchemaId, [this.objectId], [EventMembershipTypes.speaker]);
        let ids = Object.values(speakers).map(item => item.map(membership => membership.userId)).flat().filter(item => item);
        ids = Array.from(new Set(ids));
        if (ids && ids.length) {
            const activities = await this.userRepository.activity(ids as number[], 'first');
            Object.values(speakers).forEach(item => {
                item.forEach(membership => {
                    membership.userId && membership.setFirstEntranceStatus(!!activities[membership.userId]);
                })
            })
        }
        Vue.set(this, 'speakers', speakers[this.objectId] || {});
        this.speakersFetched = true;
    }

    async fetchWebinarInfo() {
        if (this.element?.beginAt && this.element?.endAt) {
            const info = await this.sessionRepository.webinar(this.agendaSchemaId, [this.objectId]);
            Vue.set(this, 'webinarInfo', info[this.objectId] || {});
        }
    }

    updateIsClosed(isClosed: boolean) {
        this.element && Vue.set(this.element, 'isClosed', isClosed);
    }

    updateIsStarted(isStarted: boolean) {
        this.element && Vue.set(this.element, 'isStarted', isStarted);
    }

    updateIsManualControlledLocal(isManualControlled: boolean) {
        this.element && Vue.set(this.element, 'isManualControlled', isManualControlled);
    }

    updateBeginAt(beginAt: DateTime) {
        this.element && Vue.set(this.element, 'beginAt', beginAt);
    }

    updateEndAt(endAt: DateTime) {
        this.element && Vue.set(this.element, 'endAt', endAt);
    }

    private async fetchTags() {
        if (this.tagSchema && this.element?.tagsIds) {
            Vue.set(this, 'tags', await this.objectRepository.query(this.tagSchema, {
                where: {
                    id: {'$in': this.element.tagsIds}
                },
                include: ['id', 'title']
            }));
        }
        else {
            Vue.set(this, 'tags', {});
        }
    }

    private async fetchLikes() {
        let likes = {};
        [likes] = await this.ratingRepository.query(this.agendaSchemaId, [this.objectId]);
        Vue.set(this, 'likes', likes || {});
    }

    private async fetchCommentsAmount() {
        const list = await this.commentRepository.count(this.agendaSchemaId, [this.objectId]) || [];
        const element = list[0] || {};
        Vue.set(this, 'commentsCount', element['count'] || 0);
    }

    private async fetchArea() {
        if (this.areaSchema && this.element?.areaId) {
            let area: any = await this.objectRepository.query(this.areaSchema, {
                where: {
                    id: this.element.areaId
                },
                include: ['title']
            });
            if (area) {
                area = (area.shift() as Element);
            }
            else {
                area = ({} as Element);
            }
            Vue.set(this, 'area', area);
        }
    }

    setLike(value: number | null) {
        this.ratingRepository.rate(this.agendaSchemaId, this.objectId, value);
    }

    updateLike(value: number | null) {
        if (value) {
            this.likes.rating = 1;
            this.likes.count = this.likes.count ? this.likes.count + 1 : 1;
        }
        else {
            this.likes.rating = null;
            this.likes.count = this.likes.count ? this.likes.count - 1 : 0;
        }
        Vue.set(this, 'likes', this.likes);
    }

    completeSession(schemaId: string, objectId: string) {
        this.objectRepository.update(schemaId, objectId, {
            isStarted: true,
            isClosed: true
        });
    }

    startSession(schemaId: string, objectId: string) {
        this.objectRepository.update(schemaId, objectId, {
            isClosed: false,
            isStarted: true
        });
    }

    updateBeginTime(schemaId: string, objectId: string, beginAt: string) {
        this.objectRepository.update(schemaId, objectId, { beginAt: beginAt });
    }

    updateEndTime(schemaId: string, objectId: string, endAt: string) {
        this.objectRepository.update(schemaId, objectId, { endAt: endAt });
    }

    updateIsManualControlled(schemaId: string, objectId: string, isManualControlled: boolean) {
        this.objectRepository.update(schemaId, objectId, { isManualControlled: isManualControlled, isStarted: isManualControlled });
    }
}
